// src/components/ui/Field.jsx
//
// R5 B2.5 — THE FORM KIT, rule 9 (desktop standard, AMENDMENT 2026-10-26).
// ONE field for every dialog on the facility side: the label ABOVE the
// control, 12/600 uppercase muted, with NO colon (the old dialogs said
// "Name (Optional):" in bold, "Genus *" in sentence case, and NAME in
// uppercase — three generations on three dialogs); a 36px control with a
// 1px #d1d5db border and an 8px radius; help under it in 12px muted; an
// error under it as a red sentence (`role="alert"`). "Required" is a muted
// WORD in the label, never a red asterisk.
//
// The control is the caller's own element — Field never owns a value, a
// handler or a `required`/`disabled` attribute; it only labels it. A plain
// <input>/<select>/<textarea> child is given the field's id (unless it has
// one), the `ff-field__control` class and `aria-describedby` for the help
// and the error. A COMPONENT child (a picker) is left alone: pass `htmlFor`
// to point the label at its control, or `group` when several controls
// share one label (the label becomes the group's name).
import React, { useId } from 'react';
import PropTypes from 'prop-types';
import './kit.css';

const HOST_CONTROLS = new Set(['input', 'select', 'textarea']);

const join = (...parts) => parts.filter(Boolean).join(' ');

/** A DOM-safe id from React's useId (":r3:" -> "ff-r3"). */
export const useFieldId = (prefix = 'ff-field') => {
  const raw = useId();
  return `${prefix}-${raw.replace(/[^A-Za-z0-9_-]/g, '')}`;
};

/** The label's own text plus the muted "required" (or "optional") word.
 *  R5 B4 review (NIT d): "optional" is the same muted word, on the labels
 *  that used to say "(optional)". */
export function LabelText({ children, required, optional }) {
  return (
    <>
      {children}
      {required ? <span className="ff-field__required ff-label__required">required</span> : null}
      {!required && optional ? (
        <span className="ff-field__required ff-field__optional ff-label__required">optional</span>
      ) : null}
    </>
  );
}

LabelText.propTypes = {
  children: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
};

export default function Field({
  label, children, help, error, required, optional, htmlFor, group, className, ...rest
}) {
  const baseId = useFieldId();
  const child = React.Children.count(children) === 1 && React.isValidElement(children)
    ? children : null;
  const host = child && typeof child.type === 'string' && HOST_CONTROLS.has(child.type);
  const controlId = (host && child.props.id) || htmlFor || `${baseId}-control`;
  const labelId = `${baseId}-label`;
  const helpId = help ? `${baseId}-help` : null;
  const errorId = error ? `${baseId}-error` : null;

  let control = children;
  if (host && !group) {
    control = React.cloneElement(child, {
      id: controlId,
      className: join(child.props.className, 'ff-field__control'),
      'aria-describedby': join(child.props['aria-describedby'], helpId, errorId) || undefined,
      'aria-invalid': error ? 'true' : child.props['aria-invalid'],
    });
  }

  return (
    <div
      className={join('ff-field', group && 'ff-field--group', className)}
      {...(group ? { role: 'group', 'aria-labelledby': labelId } : {})}
      {...rest}
    >
      {/* R5 B6: a group whose controls have a FIRST one (a select and its
          amount, a list of rows) passes `htmlFor` too — the label is then a
          real <label> tied to that control AND still names the group. A
          group of buttons (a toggle) keeps the span: a label tied to a
          button would press it. */}
      {group && htmlFor ? (
        <label className="ff-field__label" id={labelId} htmlFor={htmlFor}>
          <LabelText required={required} optional={optional}>{label}</LabelText>
        </label>
      ) : group ? (
        <span className="ff-field__label" id={labelId}>
          <LabelText required={required} optional={optional}>{label}</LabelText>
        </span>
      ) : (
        <label className="ff-field__label" id={labelId} htmlFor={controlId}>
          <LabelText required={required} optional={optional}>{label}</LabelText>
        </label>
      )}
      {group ? <div className="ff-field__controls">{control}</div> : control}
      {help ? <p className="ff-field__help" id={helpId}>{help}</p> : null}
      {error ? <p className="ff-field__error" id={errorId} role="alert">{error}</p> : null}
    </div>
  );
}

Field.propTypes = {
  /** The label's words — no colon, no asterisk. */
  label: PropTypes.node.isRequired,
  /** The control: one input/select/textarea, a picker component, or (with
   *  `group`) several controls. */
  children: PropTypes.node,
  /** 12px muted text under the control. */
  help: PropTypes.node,
  /** A red sentence under the control (role="alert"). */
  error: PropTypes.node,
  /** Adds the muted word "required" to the label. Does NOT set the
   *  control's own `required` attribute — the caller keeps that. */
  required: PropTypes.bool,
  /** Adds the muted word "optional" to the label (never with `required`). */
  optional: PropTypes.bool,
  /** The id of the control inside a COMPONENT child — or, with `group`,
   *  of the group's FIRST control (the label is then a real <label>). */
  htmlFor: PropTypes.string,
  /** Several controls under one label: a role="group" named by it. */
  group: PropTypes.bool,
  className: PropTypes.string,
};
